import { TAU, WHITE, circle, glow, rim, star } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

const BODY = '#2b2f8f';
const GLASS = 'rgba(10,8,40,.85)';
const GLASS_RIM = '#cfe8ff';
const GALAXY = '#ffd27a';
const OUT = 'rgba(4,6,18,.9)';

const TORSO = { width: 7.5, height: 12 } as const;
export const HELMET = { along: 2, radius: 9, galaxy: 6, turns: 1.8 } as const;
export const HANDS = { left: [10, -10], right: [10, 10] } as const;
export const HELD_STAR = { along: 12, across: 10, sparkle: 0.4, hue: 1 } as const;

const STAR_HUES = ['#ff5fd2', '#4aa8ff', '#b8f04a', '#ffd27a'] as const;
export const STAR_TINTS = STAR_HUES.map((hue) => Number.parseInt(hue.slice(1), 16));
export const SPARKLE = { points: 16, waist: 2.2, small: 8, smallWaist: 1.6, core: 2.6 } as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function flowing(ctx: Ctx, radius: number, hue: string, white: number): CanvasGradient {
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
  gradient.addColorStop(0, WHITE);
  gradient.addColorStop(white, hue);
  gradient.addColorStop(1, 'rgb(0 0 0 / 0)');
  return gradient;
}

function points(hue: string, fine: number): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.scale(fine, fine);
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = flowing(ctx, SPARKLE.points, hue, 0.35);
    star(ctx, 4, SPARKLE.points, SPARKLE.waist);
    ctx.fill();
  };
}

function heart(hue: string, fine: number): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.scale(fine, fine);
    ctx.save();
    ctx.rotate(Math.PI / 4);
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = flowing(ctx, SPARKLE.small, hue, 0.5);
    star(ctx, 4, SPARKLE.small, SPARKLE.smallWaist);
    ctx.fill();
    ctx.restore();
    circle(ctx, SPARKLE.core);
    ctx.fillStyle = WHITE;
    ctx.fill();
  };
}

function whole(hue: string, fine: number): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    points(hue, fine)(ctx);
    ctx.restore();
    heart(hue, fine)(ctx);
  };
}

function sparkles(
  size: number,
  draw: (hue: string, fine: number) => (ctx: Ctx) => void,
  fine: number,
): readonly Piece[] {
  const cell = Math.ceil(2 * size * fine) + 4;
  return STAR_HUES.map((hue) => [cell, cell, size * fine, draw(hue, fine)]);
}

const STARS = sparkles(SPARKLE.points, whole, 1);
const POINTS = sparkles(SPARKLE.points, points, 2);
const HEARTS = sparkles(SPARKLE.small, heart, 2);

function nth(pieces: readonly Piece[], index: number): Piece {
  const piece = pieces[index];
  if (piece === undefined) {
    throw new Error(`No sparkle of hue ${String(index)}`);
  }
  return piece;
}

function torsoPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, TORSO.width, TORSO.height, 0, 0, TAU);
}

function torso(ctx: Ctx): void {
  torsoPath(ctx);
  ctx.fillStyle = BODY;
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1.5, 0, TORSO.width / 2, TORSO.height * 0.66, 0, 0, TAU);
  ctx.fillStyle = 'rgb(255 255 255 / 0.2)';
  ctx.fill();
  torsoPath(ctx);
  ctx.lineWidth = 2;
  ctx.strokeStyle = OUT;
  ctx.stroke();
}

function silhouette(ctx: Ctx): void {
  torsoPath(ctx);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.3);
}

function helmet(ctx: Ctx): void {
  circle(ctx, HELMET.radius);
  ctx.fillStyle = GLASS;
  ctx.fill();
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = GLASS_RIM;
  ctx.stroke();
  ctx.fillStyle = WHITE;
  for (let index = 0; index < 7; index += 1) {
    const reach = 5 * ((index * 0.37) % 1);
    const across = 5 * ((index * 0.61) % 1);
    circle(ctx, 0.6, Math.cos(index * 2.4) * reach, Math.sin(index * 2.4) * across);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(0, 0, HELMET.radius * 0.8, 3.5, 4.5);
  ctx.globalAlpha = 0.7;
  ctx.lineCap = 'round';
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function galaxy(ctx: Ctx): void {
  ctx.beginPath();
  for (let step = 0; step <= 30; step += 1) {
    const share = step / 30;
    const angle = share * TAU * HELMET.turns;
    ctx.lineTo(Math.cos(angle) * share * HELMET.galaxy, Math.sin(angle) * share * HELMET.galaxy);
  }
  ctx.lineCap = 'round';
  ctx.lineWidth = 1.1;
  ctx.strokeStyle = GALAXY;
  glow(ctx, GALAXY, 3);
  ctx.stroke();
  circle(ctx, 1.1);
  ctx.fillStyle = '#fff6dc';
  ctx.fill();
}

export const LINE = { length: 64, ribbon: 8, thread: 2 } as const;
function ribbon(ctx: Ctx): void {
  const half = LINE.length / 2;
  const gradient = ctx.createLinearGradient(-half, 0, half, 0);
  gradient.addColorStop(0, STAR_HUES[0]);
  gradient.addColorStop(1, STAR_HUES[1]);
  ctx.fillStyle = gradient;
  ctx.fillRect(-half, -LINE.ribbon / 2, LINE.length, LINE.ribbon);
}

function thread(ctx: Ctx): void {
  ctx.fillStyle = WHITE;
  ctx.fillRect(-LINE.length / 2, -LINE.thread / 2, LINE.length, LINE.thread);
}

export const DOT = 4;
function dot(ctx: Ctx): void {
  circle(ctx, DOT);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

export const METEOR_TAIL = { length: 96, head: 7 } as const;
function meteor(ctx: Ctx): void {
  const { length, head } = METEOR_TAIL;
  const gradient = ctx.createLinearGradient(-length / 2, 0, length / 2 - head, 0);
  gradient.addColorStop(0, 'rgb(255 255 255 / 0)');
  gradient.addColorStop(1, WHITE);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.moveTo(-length / 2, 0);
  ctx.lineTo(length / 2 - head, -head * 0.75);
  ctx.lineTo(length / 2 - head, head * 0.75);
  ctx.closePath();
  ctx.fill();
  circle(ctx, head, length / 2 - head - 1);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

export const PORTAL = { width: 120, height: 44, rings: 3 } as const;
function portal(ctx: Ctx): void {
  ctx.strokeStyle = WHITE;
  for (let index = 0; index < PORTAL.rings; index += 1) {
    const share = 1 - index * 0.27;
    ctx.beginPath();
    ctx.ellipse(0, 0, (PORTAL.width / 2 - 4) * share, (PORTAL.height / 2 - 3) * share, 0, 0, TAU);
    ctx.lineWidth = index === 0 ? 3.2 : 2.2;
    ctx.stroke();
  }
}

function portalSpiral(ctx: Ctx): void {
  ctx.beginPath();
  for (let step = 0; step <= 40; step += 1) {
    const share = step / 40;
    const angle = share * TAU * 2;
    ctx.lineTo(Math.cos(angle) * share * 40, Math.sin(angle) * share * 40);
  }
  ctx.lineCap = 'round';
  ctx.lineWidth = 2;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const CELESTE_PIECES = {
  torso: [38, 54, REFERENCE_RADIUS, units(torso)],
  silhouette: [36, 52, REFERENCE_RADIUS, units(silhouette)],
  hand: [16, 16, REFERENCE_RADIUS, units(hand)],
  helmet: [42, 42, REFERENCE_RADIUS, units(helmet)],
  galaxy: [30, 30, REFERENCE_RADIUS, units(galaxy)],
  star0: nth(STARS, 0),
  star1: nth(STARS, 1),
  star2: nth(STARS, 2),
  star3: nth(STARS, 3),
  shadow: soft(22, 28),
} satisfies Record<string, Piece>;

// The points of a star apart from its heart, so that they alone lengthen on the beat.
export const SKY_PIECES = {
  points0: nth(POINTS, 0),
  points1: nth(POINTS, 1),
  points2: nth(POINTS, 2),
  points3: nth(POINTS, 3),
  heart0: nth(HEARTS, 0),
  heart1: nth(HEARTS, 1),
  heart2: nth(HEARTS, 2),
  heart3: nth(HEARTS, 3),
  dot: [2 * DOT + 4, 2 * DOT + 4, DOT, dot],
  ribbon: [LINE.length, LINE.ribbon + 4, LINE.length / 2, ribbon],
  thread: [LINE.length, LINE.thread + 4, LINE.length / 2, thread],
  meteor: [METEOR_TAIL.length, 20, METEOR_TAIL.length / 2, meteor],
  portal: [PORTAL.width, PORTAL.height, PORTAL.width / 2, portal],
  portalSpiral: [88, 88, 40, portalSpiral],
} satisfies Record<string, Piece>;
